/**
 * Addressbooks are exported to BigQuery when this composite index starts with
 * `true_false`. After a successful export, the cron sets it to `true_true_{key}`.
 */
export const pendingAddressbookBqExportIndex = (
  hasValidPartyId: boolean | undefined | null,
  addressbookKey: string
): string => `${hasValidPartyId === true}_false_${addressbookKey}`;

export const hasCommunityLeaderMapping = (
  communityLeaderId: string | null | undefined
): boolean => communityLeaderId != null && communityLeaderId !== '';

export type CommunityLeaderMappingStatus = 'valid' | 'invalid' | 'none';

/**
 * A cleared/null mapping is not an error. "Invalid Mapping" is only for a
 * leader ID that failed validation.
 */
export const getCommunityLeaderMappingStatus = ({
  communityLeaderId,
  communityLeaderMappingValid,
}: {
  communityLeaderId?: string | null;
  communityLeaderMappingValid?: boolean | null;
}): CommunityLeaderMappingStatus => {
  if (communityLeaderMappingValid === true) {
    return 'valid';
  }

  if (
    communityLeaderMappingValid === false &&
    hasCommunityLeaderMapping(communityLeaderId)
  ) {
    return 'invalid';
  }

  return 'none';
};

export type ProcessedAddressbookUpdateInput = {
  processed: string | boolean;
  partyIdValid: boolean | undefined | null;
  addressbookKey: string;
  addressbookId: string | number;
  modified: string;
};

/**
 * Builds the RTDB patch for a Processed toggle.
 *
 * Unprocessing unlocks other addressbook fields for edit. It must not clear the
 * community leader mapping; removal is explicit from the mapping modal.
 */
export const buildProcessedAddressbookUpdate = ({
  processed,
  partyIdValid,
  addressbookKey,
  addressbookId,
  modified,
}: ProcessedAddressbookUpdateInput): Record<string, unknown> => {
  const processedFlag = `${processed}`;
  const hasValidPartyId = partyIdValid === true;

  return {
    haspartyid_processed_accountid_addressbookid: `${hasValidPartyId}_${processedFlag}_${addressbookKey}`,
    haspartyid_processed_addressbookid: `${hasValidPartyId}_${processedFlag}_${addressbookId}`,
    modified,
  };
};

export type CommunityLeaderMappingSaveInput = {
  partyIdValid: boolean | undefined | null;
  addressbookKey: string;
  communityLeaderId: string | null | undefined;
  communityLeaderName: string | null | undefined;
  modified: string;
};

/**
 * Builds the RTDB patch when a community leader mapping is set or cleared from
 * the mapping modal. Both paths queue a BigQuery export.
 */
export const buildCommunityLeaderMappingUpdate = ({
  partyIdValid,
  addressbookKey,
  communityLeaderId,
  communityLeaderName,
  modified,
}: CommunityLeaderMappingSaveInput): Record<string, unknown> => {
  const hasValidPartyId = partyIdValid === true;

  if (hasCommunityLeaderMapping(communityLeaderId)) {
    return {
      communityLeaderId,
      communityLeaderName: communityLeaderName || null,
      communityLeaderMappingValid: true,
      haspartyid_senttobq_accountid_addressbookid:
        pendingAddressbookBqExportIndex(hasValidPartyId, addressbookKey),
      modified,
    };
  }

  return {
    communityLeaderId: null,
    communityLeaderName: null,
    communityLeaderMappingValid: null,
    haspartyid_senttobq_accountid_addressbookid:
      pendingAddressbookBqExportIndex(hasValidPartyId, addressbookKey),
    modified,
  };
};
